import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import { Bem } from '../../service/Bem/Bem';
import { getRetargetingConditions } from '../../service/RetargetingHelpers/RetargetingHelpers';
import { getUserProfileConditions } from '../../service/UserProfileHelpers/UserProfileHelpers';
import {
    CopyTooltipStatus,
    RetargetingConditionRule
} from '../../typings/domain';
import { UserProfileCondition } from '../../typings/userProfileRetargeting';
import {
    UsersProfileConditionInfoTooltip,
    UsersProfileConditionInfoTooltipProps
} from '../UsersProfileConditionInfoTooltip/UsersProfileConditionInfoTooltip';

import './UsersProfileConditionInfo.css';
import * as keyset from './UsersProfileConditionInfo.i18n';

const i18n = i18nFactory(keyset);

export interface UsersProfileConditionInfoProps {
    userProfileConditions: Array<UserProfileCondition>;
    metrikaAudienceGoals: RetargetingConditionRule[];
    isHasConditions: boolean;
    onCopyClick: (description: string) => void;
    copyTooltipStatus: CopyTooltipStatus;
    onPopupOpen: () => void;
}

const BLOCK = 'users-profile-condition-info';

const GridPopupContainer = makeGridPopupContainer<
    UsersProfileConditionInfoTooltipProps
>(UsersProfileConditionInfoTooltip);

export const UsersProfileConditionInfo = React.memo(
    (props: UsersProfileConditionInfoProps) => {
        const {
            userProfileConditions,
            metrikaAudienceGoals,
            isHasConditions,
            copyTooltipStatus,
            onPopupOpen,
            onCopyClick
        } = props;

        return (
            <div className={classNames(Bem.cls(BLOCK))}>
                <div className={Bem.cls(BLOCK, 'description')}>
                    {isHasConditions
                        ? getConditions(
                              userProfileConditions,
                              metrikaAudienceGoals
                          )
                        : i18n('all-audience')}
                </div>
                {isHasConditions && (
                    <GridPopupContainer
                        onPopupOpen={onPopupOpen}
                        userProfileConditions={userProfileConditions}
                        metrikaAudienceGoals={metrikaAudienceGoals}
                        copyTooltipStatus={copyTooltipStatus}
                        onCopyClick={() =>
                            onCopyConditions(
                                onCopyClick,
                                userProfileConditions,
                                metrikaAudienceGoals
                            )
                        }
                    />
                )}
            </div>
        );
    }
);

const getConditions = (
    userProfileConditions: Array<UserProfileCondition>,
    metrikaAudienceGoals: RetargetingConditionRule[]
) => {
    const userProfilesConditionsText = getUserProfileConditions(
        userProfileConditions,
        false
    );
    const retargetingConditionsText = getRetargetingConditions(
        metrikaAudienceGoals,
        false
    );
    const delim =
        userProfilesConditionsText && retargetingConditionsText ? '; ' : '';

    return `${userProfilesConditionsText}${delim}${retargetingConditionsText}`;
};

const onCopyConditions = (
    onCopyClick: (description: string) => void,
    userProfileConditions: Array<UserProfileCondition>,
    metrikaAudienceGoals: RetargetingConditionRule[]
) => {
    const userProfilesConditionsText = getUserProfileConditions(
        userProfileConditions,
        true
    );
    const retargetingConditionsText = getRetargetingConditions(
        metrikaAudienceGoals,
        true
    );
    const userProfileText = userProfilesConditionsText
        ? `${i18n('users-profiles')}\n${userProfilesConditionsText}`
        : '';
    const metrikaText = retargetingConditionsText
        ? `${i18n('metrikaAudience')}\n${retargetingConditionsText}`
        : '';
    const delim =
        userProfilesConditionsText && retargetingConditionsText ? '\n\n' : '';

    onCopyClick(`${userProfileText}${delim}${metrikaText}`);
};
